import { useState, useEffect, useRef } from "react";
import { verifyGSTIN } from "../../services/flightBookingAPI";

function isValidGSTINBasic(gstin) {
  if (!gstin || typeof gstin !== "string") return false;

  gstin = gstin.trim().toUpperCase();

  // 1. Length check
  if (gstin.length !== 15) return false;

  // 2. First 2 characters must be numeric (state code)
  if (!/^\d{2}/.test(gstin)) return false;

  // 3. Must be alphanumeric
  if (!/^[0-9A-Z]+$/.test(gstin)) return false;

  return true;
}

const GSTBillingDetails = ({ onChange, isGstMandatory = false }) => {
  // Toggle ON  → show GSTIN input only
  // Toggle OFF → show Billing Address only
  const [gstEnabled, setGstEnabled] = useState(isGstMandatory || false);

  // Update gstEnabled if isGstMandatory change (though usually static for flight)
  useEffect(() => {
    if (isGstMandatory) {
      setGstEnabled(true);
    }
  }, [isGstMandatory]);
  const [gstin, setGstin] = useState("");
  
  const [gstinLoading, setGstinLoading] = useState(false);
  const [gstinError, setGstinError] = useState("");
  const [gstinSuccessMessage, setGstinSuccessMessage] = useState("");
  const [verifiedCompanyName, setVerifiedCompanyName] = useState("");

  const isGstinFormatValid = isValidGSTINBasic(gstin);

  // AUTO-TRIGGER: Call handleCheckGSTIN as soon as the format is valid (15 chars)
  useEffect(() => {
    if (isGstinFormatValid && !gstinLoading && !gstinSuccessMessage && !gstinError) {
      // Debounce slightly to avoid immediate call while still finishing typing (optional but safer)
      const timer = setTimeout(() => {
        handleCheckGSTIN();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [gstin, isGstinFormatValid]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleGstinChange = (e) => {
    setGstin(e.target.value.toUpperCase());
    setGstinError("");
    setGstinSuccessMessage("");
    setVerifiedCompanyName("");
  };

  const handleCheckGSTIN = async () => {
    if (!isValidGSTINBasic(gstin)) return;
    
    setGstinLoading(true);
    setGstinError("");
    setGstinSuccessMessage("");
    setVerifiedCompanyName("");
    
    try {
      const response = await verifyGSTIN(gstin);
      
      if (response && response.data && response.data.exists) {
        setGstinSuccessMessage("Verified!");
        setVerifiedCompanyName(response.data.legal_name);
      } else {
        setGstinError("GSTIN does not exist");
      }
    } catch (error) {
      setGstinError(error.message || "Failed to verify GSTIN. Please try again.");
    } finally {
      setGstinLoading(false);
    }
  };

  // Billing Address state
  const [pincode, setPincode] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  // Pincode lookup state
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState("");
  const debounceRef = useRef(null);

  // Auto-fetch city & state when pincode reaches 6 digits
  useEffect(() => {
    // Clear previous error on every change
    setPincodeError("");

    if (pincode.length !== 6) {
      // Reset city/state if pincode is cleared/incomplete
      if (pincode.length < 6) {
        setCity("");
        setState("");
      }
      return;
    }

    // Debounce: wait 400ms after user stops typing
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setPincodeLoading(true);
      setPincodeError("");
      setCity("");
      setState("");

      try {
        const res = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`
        );
        const data = await res.json();

        if (
          data &&
          data[0]?.Status === "Success" &&
          data[0]?.PostOffice?.length > 0
        ) {
          const po = data[0].PostOffice[0];
          setCity(po.District || po.Block || "");
          setState(po.State || "");
        } else {
          setPincodeError("Invalid pincode. Please check and try again.");
        }
      } catch {
        setPincodeError("Could not fetch pincode details. Check your connection.");
      } finally {
        setPincodeLoading(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pincode]);

  const lastGstStateRef = useRef(null);

  // Expose state to parent component
  useEffect(() => {
    if (onChange) {
      const gstinVerified = !!gstinSuccessMessage;
      const currentGstData = {
        gstEnabled,
        gstin,
        pincode,
        address,
        city,
        state,
        isVerified: gstinVerified
      };

      // Robust Solution: JSON comparison stops the infinite loop by only calling onChange 
      // when the ACTUAL values have changed, regardless of reference changes in parent.
      const lastDataStr = JSON.stringify(lastGstStateRef.current);
      const currentDataStr = JSON.stringify(currentGstData);

      if (lastDataStr !== currentDataStr) {
        lastGstStateRef.current = currentGstData;
        onChange(currentGstData);
      }
    }
  }, [gstEnabled, gstin, pincode, address, city, state, gstinSuccessMessage, onChange]);

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* ── Card Header ── */}
      <div className="flex items-center gap-3 bg-[#FFF6EC] px-6 py-4">
        <svg
          className="w-5 h-5 text-orange-500 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
          />
        </svg>
        <h2 className="text-base font-semibold text-orange-500">
          {gstEnabled ? "GST Details" : "Billing Address"}
        </h2>
      </div>

      {/* ── Card Body ── */}
      <div className="p-6">
        {/* Subtext */}
        <p className="text-sm text-gray-500 mb-5">
          {gstEnabled
            ? "To claim credit of GST charged by airlines, please enter your GST details"
            : "As per the latest govt. regulations, it's mandatory to provide your address."}
        </p>

        {/* Toggle Row — always visible */}
        <div className="flex items-center gap-3 mb-5">
          <button
            type="button"
            role="switch"
            aria-checked={gstEnabled}
            onClick={() => !isGstMandatory && setGstEnabled((prev) => !prev)}
            disabled={isGstMandatory}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${gstEnabled ? "bg-orange-500" : "bg-gray-300"
              } ${isGstMandatory ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${gstEnabled ? "translate-x-5" : "translate-x-0"
                }`}
            />
          </button>
          <span className="text-sm font-medium text-gray-700">
            I would like to add my GST Number {isGstMandatory && <span className="text-red-500 ml-1 font-bold">(Required)</span>}
          </span>
        </div>

        {/* ── Toggle ON: GSTIN Input ── */}
        {gstEnabled && (
          <div className="mt-2 text-left">
            <div className={`relative flex items-center border rounded-md overflow-hidden bg-white focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500 transition-all ${gstinError ? "border-red-400" : (gstinSuccessMessage ? "border-green-400" : "border-[#11111333]")}`}>
              <label className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-orange-500 pointer-events-none select-none">
                GSTIN
              </label>
              <input
                type="text"
                value={gstin}
                onChange={handleGstinChange}
                maxLength={15}
                placeholder=""
                disabled={gstinLoading}
                className="flex-1 px-4 py-3 pl-16 text-gray-700 text-sm focus:outline-none bg-transparent h-[50px] disabled:cursor-not-allowed"
              />
              {!gstinSuccessMessage ? (
                <button
                  type="button"
                  onClick={handleCheckGSTIN}
                  disabled={!isGstinFormatValid || gstinLoading}
                  className={`px-4 py-2 mr-2 text-sm font-bold transition-colors flex items-center justify-center gap-2 w-[72px] ${(!isGstinFormatValid || gstinLoading) ? "text-gray-400 cursor-not-allowed" : "text-orange-500 hover:text-orange-600"}`}
                >
                  {gstinLoading ? (
                    <svg
                      className="animate-spin h-6 w-6 text-orange-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>
                  ) : (
                    "Check"
                  )}
                </button>
              ) : (
                <div className="px-4 py-2 flex items-center justify-center mr-1">
                  <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            
            {/* GSTIN Error */}
            {gstin && !isGstinFormatValid && (
              <p className="mt-2 text-sm text-red-500 flex items-center gap-1.5">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Invalid GSTIN format
              </p>
            )}
            {gstinError && (
              <p className="mt-2 text-sm text-red-500 flex items-center gap-1.5">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {gstinError}
              </p>
            )}

            {/* GSTIN Success */}
            {gstinSuccessMessage && verifiedCompanyName && (
              <p className="mt-2 text-sm text-gray-500">
                Company Name: {verifiedCompanyName}
              </p>
            )}
          </div>
        )}

        {/* ── Toggle OFF: Billing Address Fields ── */}
        {!gstEnabled && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Pincode — with loading spinner inside */}
            <div className="relative">
              <input
                type="text"
                value={pincode}
                onChange={(e) =>
                  setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="Pincode *"
                maxLength={6}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all h-[50px] pr-10 ${pincodeError ? "border-red-400" : "border-[#11111333]"
                  }`}
              />
              {/* Loading spinner */}
              {pincodeLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg
                    className="animate-spin h-6 w-6 text-orange-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                </div>
              )}
              {/* Green check when filled successfully */}
              {!pincodeLoading && city && state && pincode.length === 6 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>

            {/* Address */}
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Address *"
              className="w-full px-4 py-3 border border-[#11111333] rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all h-[50px]"
            />

            {/* City — disabled, auto-filled */}
            <div className="relative">
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                className="w-full px-4 py-3 border border-[#11111333] rounded-md text-gray-700 transition-all h-[50px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* Optional icon */}
              {/* <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div> */}
            </div>

            {/* State — disabled, auto-filled */}
            <div className="relative">
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="State"
                  className="w-full px-4 py-3 border border-[#11111333] rounded-md text-gray-700 transition-all h-[50px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {/* Optional icon */}
                {/* <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div> */}
            </div>

            {/* Pincode error message — spans both columns */}
            {pincodeError && (
              <p className="lg:col-span-2 text-sm text-red-500 flex items-center gap-1.5 -mt-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {pincodeError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GSTBillingDetails;
